import {
    markShipmentMilestone,
    type ShipmentMilestone,
    updateShipmentFreight,
} from '@api/mother-order';
import type { Shipment } from '@api/shipments';
import { isFreightForwarder, listVendors, type VendorListItem } from '@api/vendors';
import { DateSinglePicker } from '@components/ui/DateRange';
import useToaster from '@hooks/toaster/useToaster';
import { listVendorTermsOptions } from '@diagon-alley/common';
import { Button, Group, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useState } from 'react';

import { CURRENCY_OPTIONS, FieldLabel, formatDateDisplay, textColors } from '../procurementOrders/shared';
import { shipmentNeeds } from '../orders/inbound';
import { NeedChips } from '../orders/NeedChip';

const errMessage = (e: any, fallback: string): string => e?.error?.message || e?.message || fallback;

/**
 * The dispatch booking and the three facts of a shipment's journey, on the shipment itself.
 *
 * Booking: who moves it (a vendor tagged as a freight forwarder), on which of its terms, for
 * how much, and when it is expected to leave. Saved as one call; the forwarder's payment
 * tranches follow the booking onto the Payments page.
 *
 * Facts: Mark dispatched / arrived / delivered. Each is a stamp the status derives from; the
 * plan dates in the timeline above are never promoted on their own. Undo is a small link,
 * because a mis-click should be a mis-click and not a support ticket.
 */
export const DispatchBlock: FC<{
    shipment: Shipment;
    onChanged: () => void;
}> = ({ shipment, onChanged }) => {
    const { showToastSuccess, showToastError } = useToaster();
    const motherId = shipment.motherOrderId as string;
    const { data: vendors } = useQuery(['vendors-all-for-forwarder'], listVendors, { staleTime: 60_000 });
    const forwarders = useMemo(() => (vendors ?? []).filter(isFreightForwarder), [vendors]);

    const [forwarderId, setForwarderId] = useState<string | null>(shipment.forwarderVendorId);
    const [termsId, setTermsId] = useState<string | null>(shipment.forwarderTermsOptionId);
    const [freight, setFreight] = useState<number | ''>(shipment.freightAmount ?? '');
    const [currency, setCurrency] = useState<string>(shipment.freightCurrency ?? shipment.currency ?? 'INR');
    const [tracking, setTracking] = useState(shipment.trackingRef ?? '');
    const [departure, setDeparture] = useState(shipment.departureDate ?? '');
    const [saving, setSaving] = useState<string | null>(null);

    useEffect(() => {
        setForwarderId(shipment.forwarderVendorId);
        setTermsId(shipment.forwarderTermsOptionId);
        setFreight(shipment.freightAmount ?? '');
        setCurrency(shipment.freightCurrency ?? shipment.currency ?? 'INR');
        setTracking(shipment.trackingRef ?? '');
        setDeparture(shipment.departureDate ?? '');
    }, [shipment]);

    const forwarder: VendorListItem | undefined = forwarders.find((v) => v.vendorId === forwarderId);
    const termsOptions = useMemo(
        () => (forwarder ? listVendorTermsOptions(forwarder.paymentTermsConfig) : []),
        [forwarder],
    );
    // One set applies on its own; two or more need a pick; a pick that no longer exists is dropped.
    useEffect(() => {
        if (termsOptions.length === 1) setTermsId(termsOptions[0].id);
        else if (termsId && !termsOptions.some((o) => o.id === termsId)) setTermsId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- follows the forwarder's options only
    }, [termsOptions]);

    const dirty =
        (forwarderId ?? null) !== (shipment.forwarderVendorId ?? null) ||
        (termsId ?? null) !== (shipment.forwarderTermsOptionId ?? null) ||
        (freight === '' ? null : Number(freight)) !== (shipment.freightAmount ?? null) ||
        currency !== (shipment.freightCurrency ?? shipment.currency ?? 'INR') ||
        tracking.trim() !== (shipment.trackingRef ?? '') ||
        (departure || null) !== (shipment.departureDate || null);

    const saveBooking = async () => {
        if (forwarderId && termsOptions.length > 1 && !termsId) {
            showToastError({ title: 'Pick the forwarder’s payment terms', subtitle: `${forwarder?.name} offers ${termsOptions.length} sets.` });
            return;
        }
        setSaving('booking');
        try {
            await updateShipmentFreight(motherId, shipment.poNumber, {
                forwarderVendorId: forwarderId,
                forwarderTermsOptionId: forwarderId ? termsId : null,
                freightAmount: freight === '' ? null : Number(freight),
                freightCurrency: currency,
                trackingRef: tracking.trim() || null,
                departureDate: departure || null,
            });
            showToastSuccess({ title: forwarderId ? 'Dispatch booked' : 'Booking saved' });
            onChanged();
        } catch (e: any) {
            showToastError({ title: 'Could not save the booking', subtitle: errMessage(e, 'Please retry.') });
        } finally {
            setSaving(null);
        }
    };

    const mark = async (milestone: ShipmentMilestone, clear = false) => {
        setSaving(milestone);
        try {
            await markShipmentMilestone(motherId, shipment.poNumber, { milestone, clear });
            showToastSuccess({
                title: clear
                    ? 'Taken back'
                    : milestone === 'DISPATCH'
                      ? 'Marked dispatched'
                      : milestone === 'ARRIVAL'
                        ? 'Marked arrived'
                        : 'Marked delivered',
            });
            onChanged();
        } catch (e: any) {
            showToastError({ title: 'Could not record that', subtitle: errMessage(e, 'Please retry.') });
        } finally {
            setSaving(null);
        }
    };

    const needs = shipmentNeeds(shipment);
    const done = !!shipment.manualReceivedAt || shipment.status === 'RECEIVED' || !!shipment.cancelledAt;

    return (
        <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <Group justify="space-between" align="center" mb={10}>
                <Text size="11px" fw={700} c={textColors.secondary} style={{ letterSpacing: '0.06em' }}>
                    DISPATCH
                </Text>
                <NeedChips needs={needs} />
            </Group>

            {!shipment.shippedAt && !done && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                        <div>
                            <FieldLabel>Freight forwarder</FieldLabel>
                            <Select
                                data={forwarders.map((v) => ({ value: v.vendorId, label: v.name }))}
                                value={forwarderId}
                                onChange={(v) => {
                                    setForwarderId(v);
                                    setTermsId(null);
                                }}
                                placeholder={forwarders.length ? 'Pick a forwarder' : 'No forwarders tagged yet'}
                                nothingFoundMessage="Tag a vendor as Freight forwarder on the Suppliers page"
                                searchable
                                clearable
                                size="xs"
                            />
                        </div>
                        {forwarderId && termsOptions.length > 1 && (
                            <div>
                                <FieldLabel>Forwarder terms</FieldLabel>
                                <Select
                                    data={termsOptions.map((o) => ({ value: o.id, label: o.name }))}
                                    value={termsId}
                                    onChange={setTermsId}
                                    placeholder="Pick terms"
                                    size="xs"
                                />
                            </div>
                        )}
                        {forwarderId && termsOptions.length === 1 && (
                            <div>
                                <FieldLabel>Forwarder terms</FieldLabel>
                                <Text size="13px" c={textColors.primary} pt={6}>{termsOptions[0].name}</Text>
                            </div>
                        )}
                        {forwarderId && termsOptions.length === 0 && (
                            <div>
                                <FieldLabel>Forwarder terms</FieldLabel>
                                <Text size="11px" c={textColors.muted} pt={6}>No terms saved on this forwarder; nothing is scheduled for payment.</Text>
                            </div>
                        )}
                        <div>
                            <FieldLabel>Freight charge</FieldLabel>
                            <Group gap={6} wrap="nowrap">
                                <NumberInput value={freight} onChange={(v) => setFreight(typeof v === 'number' ? v : '')} min={0} size="xs" hideControls placeholder="0" />
                                <Select
                                    data={CURRENCY_OPTIONS}
                                    value={currency}
                                    onChange={(v) => v && setCurrency(v)}
                                    size="xs"
                                    w={90}
                                    allowDeselect={false}
                                    // The input stays narrow; the dropdown gets room so "INR (₹)" does not wrap.
                                    comboboxProps={{ width: 180, withinPortal: true, position: 'bottom-start' }}
                                />
                            </Group>
                        </div>
                        <div>
                            <FieldLabel>Expected dispatch</FieldLabel>
                            <DateSinglePicker value={departure} onChange={(v) => setDeparture(v || '')} placeholder="Plan" clearable />
                        </div>
                        <div>
                            <FieldLabel>Tracking / container ref</FieldLabel>
                            <TextInput value={tracking} onChange={(e) => setTracking(e.currentTarget.value)} size="xs" placeholder="BL / AWB / container" />
                        </div>
                    </div>
                    <Group justify="space-between" mt={10}>
                        <Button size="xs" variant="default" onClick={() => void saveBooking()} loading={saving === 'booking'} disabled={!dirty}>
                            Save booking
                        </Button>
                        <Button
                            size="xs"
                            onClick={() => void mark('DISPATCH')}
                            loading={saving === 'DISPATCH'}
                            title={dirty ? 'Save the booking first' : 'Records that the load left; the plan date stays as it was'}
                            disabled={dirty}
                        >
                            Mark dispatched
                        </Button>
                    </Group>
                </>
            )}

            {shipment.shippedAt && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: textColors.primary }}>
                    <Group gap={8}>
                        <span>Dispatched {formatDateDisplay(shipment.shippedAt)}</span>
                        {shipment.forwarderName ? <span style={{ color: textColors.secondary }}>· {shipment.forwarderName}</span> : null}
                        {shipment.trackingRef ? <span style={{ color: textColors.secondary }}>· {shipment.trackingRef}</span> : null}
                        {!shipment.arrivedAt && (
                            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => void mark('DISPATCH', true)} loading={saving === 'DISPATCH'}>
                                undo
                            </Button>
                        )}
                    </Group>
                    {!shipment.arrivedAt ? (
                        <Group gap={8}>
                            <Button size="xs" onClick={() => void mark('ARRIVAL')} loading={saving === 'ARRIVAL'}>Mark arrived</Button>
                            <Text size="11px" c={textColors.muted}>at the destination port / hub</Text>
                        </Group>
                    ) : (
                        <Group gap={8}>
                            <span>Arrived {formatDateDisplay(shipment.arrivedAt)}</span>
                            {!shipment.deliveredAt && (
                                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => void mark('ARRIVAL', true)} loading={saving === 'ARRIVAL'}>undo</Button>
                            )}
                        </Group>
                    )}
                    {shipment.arrivedAt && !shipment.deliveredAt && (
                        <Group gap={8}>
                            <Button size="xs" onClick={() => void mark('DELIVERY')} loading={saving === 'DELIVERY'}>Mark delivered</Button>
                            <Text size="11px" c={textColors.muted}>reached your warehouse; receipt still comes from the GRN</Text>
                        </Group>
                    )}
                    {shipment.deliveredAt && (
                        <Group gap={8}>
                            <span>Delivered {formatDateDisplay(shipment.deliveredAt)}</span>
                            <Button size="compact-xs" variant="subtle" color="gray" onClick={() => void mark('DELIVERY', true)} loading={saving === 'DELIVERY'}>undo</Button>
                        </Group>
                    )}
                </div>
            )}
        </div>
    );
};
